import { useState, useMemo } from "react";
import { formatNum } from "../../oscar-pos-core/constants";
import moment from "moment";
import { sentenceCase } from "../../constants";

export const useFiltersHandler = (activeTab, I18n = {}) => {
  const initialFilters = {
    sort: null, // single-select
    status: [], // multi-select
    method: [], // multi-select
    category: [], // multi-select
    type: [],
    amount: { min: "", max: "" }, // range
    keywords: "", // text search
    date: { startDate: null, endDate: null }, // date range
    customers: !activeTab
      ? "Customers"
      : activeTab?.isSupplier
        ? "Suppliers"
        : activeTab?.isCommunity
          ? "By others"
          : "Customers",
    items: null,
    view: null,
    visibility: null,
  };

  const [filters, setFilters] = useState(initialFilters);
  const [isResetFilter, setIsResetFilter] = useState(false);

  const handleFilterChange = (type, value) => {
    const key = type.toLowerCase();

    setFilters((prev) => {
      switch (key) {
        // SINGLE-SELECT
        case "sort":
          return { ...prev, sort: value };
        case "customers":
          return { ...prev, customers: value };
        case "view":
          return { ...prev, view: value };
        case "items":
          return { ...prev, items: value };
        case "type":
          return { ...prev, type: value };
        case "status":
          return { ...prev, status: value };
        case "visibility":
          return { ...prev, visibility: value };
        case "method":
        case "category":
          return {
            ...prev,
            [key]: Array.isArray(value) ? value : [],
          };

        // RANGE
        case "amount":
          return {
            ...prev,
            amount: { min: value.min ?? "", max: value.max ?? "" },
          };

        // DATE RANGE
        case "date":
          return {
            ...prev,
            date: {
              startDate: value.startDate ?? null,
              endDate: value.endDate ?? null,
            },
          };

        // KEYWORDS
        case "keywords":
          return { ...prev, keywords: value ?? "" };

        default:
          return prev;
      }
    });
  };

  const resetFilters = () => {
    setFilters({
      ...initialFilters,
      customers: filters?.customers,
    });
    setIsResetFilter(true);
  };

  // Fixed enum values buildFiltersConfig's default options carry (status/method/
  // type/visibility/sort) — translated here so the applied-chip VALUE display
  // (not just the dropdown option label) follows the active language too.
  // Falls back to sentenceCase for anything not in this map (e.g. dynamic
  // category names, or a screen's own customSort/customMethods values).
  const valueLabel = (val) => {
    const knownValueLabels = {
      paid: I18n.paid,
      unpaid: I18n.unpaid,
      overdue: I18n.overdue,
      Cash: I18n.cash,
      Bank: I18n.bank,
      "Cash In": I18n.cash_in,
      "Cash Out": I18n.cash_out,
      Receivables: I18n.receivables,
      Payables: I18n.payables,
      Settled: I18n.settled,
      Published: I18n.published,
      Unpublished: I18n.unpublished,
      Newest: I18n.newest,
      Oldest: I18n.oldest,
      "Amount (High to Low)": I18n.amount_high_to_low,
      "Amount (Low to High)": I18n.amount_low_to_high,
      "Name (A - Z)": I18n.filter_name_az,
      "Name (Z - A)": I18n.name_z_a,
      "Quantity (High to Low)": I18n.quantity_high_to_low,
      "Quantity (Low to High)": I18n.quantity_low_to_high,
      "Quick sale": I18n.quick_sale,
      Invoice: I18n.invoice,
      POS: I18n.pos,
      "Low stock items": I18n.low_stock_items,
    };
    return knownValueLabels[val] || sentenceCase(val);
  };

  const appliedChips = useMemo(() => {
    const chips = [];

    // Status - handles both single-select (string) and multi-select (array)
    const statusVal = filters.status;
    if (Array.isArray(statusVal) ? statusVal.length : statusVal) {
      chips.push({
        id: "status",
        label: I18n.status || "Status",
        value: Array.isArray(statusVal)
          ? statusVal.length > 1
            ? `(${statusVal.length})`
            : valueLabel(statusVal[0])
          : valueLabel(statusVal),
        count: Array.isArray(statusVal) ? statusVal.length : 1,
      });
    }

    // Multi-selects
    [
      { key: "Method", label: I18n.method || "Method" },
      { key: "Category", label: I18n.category || "Category" },
      { key: "Type", label: I18n.type || "Type" },
    ].forEach(({ key, label }) => {
      let realKey = key.toLowerCase();
      if (filters[realKey]?.length)
        chips.push({
          id: realKey,
          label,
          value:
            filters[realKey].length > 1
              ? `(${filters[realKey].length})`
              : valueLabel(filters[realKey]?.[0]),
          count: filters[realKey]?.length,
        });
    });

    // Single-select
    if (filters.sort)
      chips.push({
        id: "sort",
        label: I18n.sort || "Sort",
        value: valueLabel(filters.sort),
      });

    if (filters.visibility)
      chips.push({
        id: "visibility",
        label: I18n.visibility || "Visibility",
        value: valueLabel(filters.visibility),
      });

    if (filters.items)
      chips.push({
        id: "items",
        label: I18n.items || "Items",
        value: valueLabel(filters.items),
      });

    if (filters.view) {
      chips.push({
        id: "view",
        label: I18n.view || "View",
        value: valueLabel(filters.view),
      });
    }

    // Keywords
    if (filters.keywords)
      chips.push({
        id: "keywords",
        label: I18n.search || "Search",
        value: `${I18n.keyword || "Keyword"}: "${filters.keywords}"`,
      });

    // Amount range
    if (filters.amount.min || filters.amount.max) {
      chips.push({
        id: "amount",
        label: I18n.amount || "Amount",
        value: `${I18n.amount || "Amount"}: "Rs. ${formatNum(filters.amount.min) || 0} - ${formatNum(filters.amount.max) || "∞"}"`,
      });
    }

    // Date range
    if (filters.date.startDate || filters.date.endDate) {
      const startM = filters.date.startDate ? moment(filters.date.startDate).startOf("day") : null;
      const endM = filters.date.endDate ? moment(filters.date.endDate).startOf("day") : null;
      const DURATION_LABELS = { 7: I18n.weekly || "Weekly", 30: I18n.monthly || "Monthly", 365: I18n.yearly || "Yearly", 366: I18n.yearly || "Yearly" };
      const days = startM && endM ? endM.diff(startM, "day") + 1 : null;
      const isToday = days === 1 && startM && startM.isSame(moment().startOf("day"), "day");
      const isFullCalendarMonth =
        startM && endM && startM.date() === 1 &&
        endM.isSame(startM.clone().endOf("month").startOf("day"), "day");
      const friendlyLabel = isToday
        ? (I18n.today || "Today")
        : isFullCalendarMonth
          ? (I18n.monthly || "Monthly")
          : (days ? DURATION_LABELS[days] : null);
      chips.push({
        id: "date",
        label: I18n.date || "Date",
        value: friendlyLabel
          ? friendlyLabel
          : `${startM ? startM.format("MMM DD") : (I18n.start || "Start")} - ${endM ? endM.format("MMM DD") : (I18n.end || "End")}`,
      });
    }

    return chips;
  }, [filters]);

  const handleRemoveChip = (chip) => {
    const id = String(chip.id ?? chip.label).toLowerCase();

    switch (id) {
      case "status":
        handleFilterChange("status", Array.isArray(filters.status) ? [] : null);
        break;

      case "visibility":
        handleFilterChange("visibility", null);
        break;

      case "method":
        handleFilterChange("method", []);
        break;

      case "category":
        handleFilterChange("category", []);
        break;

      case "sort":
        handleFilterChange("sort", null);
        break;

      case "type":
        handleFilterChange("type", []);
        break;

      case "keywords":
      case "search":
        handleFilterChange("keywords", "");
        break;

      case "amount":
        handleFilterChange("amount", { min: "", max: "" });
        break;

      case "items":
        handleFilterChange("items", null);
        break;

      case "view":
        handleFilterChange("view", null);
        break;

      case "date":
        handleFilterChange("date", { startDate: null, endDate: null });
        break;

      default:
        break;
    }
  };

  const isAppliedFilter = useMemo(() => {
    return (
      filters.sort !== null ||
      (Array.isArray(filters.status)
        ? filters.status.length > 0
        : !!filters.status) ||
      filters.method.length > 0 ||
      filters.visibility !== null ||
      filters.type.length > 0 ||
      filters.category.length > 0 ||
      filters.keywords !== "" ||
      filters.amount.min !== "" ||
      filters.amount.max !== "" ||
      filters.date.startDate !== null ||
      filters.date.endDate !== null ||
      filters?.items !== null
    );
  }, [filters]);

  const isAppliedFilterOtherThenKeywords = useMemo(() => {
    return (
      filters.sort !== null ||
      (Array.isArray(filters.status)
        ? filters.status.length > 0
        : !!filters.status) ||
      filters.method.length > 0 ||
      filters.visibility !== null ||
      filters.type.length > 0 ||
      filters.category.length > 0 ||
      filters.amount.min !== "" ||
      filters.amount.max !== "" ||
      filters.date.startDate !== null ||
      filters.date.endDate !== null ||
      filters?.items !== null
    );
  }, [filters]);

  return {
    filters,
    appliedChips,
    handleFilterChange,
    resetFilters,
    handleRemoveChip,
    isAppliedFilter,
    isAppliedFilterOtherThenKeywords,
    isResetFilter,
    setIsResetFilter,
  };
};
